import { defineStore } from 'pinia'
import { reactive, ref } from 'vue'

import type { BehaviorScreen } from '@/utils/instrumentation'
import { BehaviorAction, BehaviorEventName, activeScreen, newAttemptId, trackEvent } from '@/utils/instrumentation'
import api from '@axios'

import type { ChatMessage, ServiceType, VerificationSession } from './types'

/**
 * Who asked for a verification. Required on verifyRaw(), not defaulted.
 *
 * A default would just relocate the bug to whichever case it guessed wrong:
 * default to 'auto' and the next user-facing call site added in a UX round is
 * silently uncounted — which is exactly how five of them went missing — while
 * 'user' would count the next watcher as a person.
 *
 * The type alone does not enforce this: CI runs ESLint and vitest but not
 * vue-tsc. `verifyRawCallSites.test.ts` is the real guard — it fails if any
 * verifyRaw() call in src/ omits a trigger.
 */
export type VerificationTrigger = 'user' | 'auto'

export const useIvVerificationStore = defineStore('iv-verification', () => {
  const session = ref<VerificationSession | null>(null)
  const history = ref<VerificationSession[]>([])
  const chatMessages = ref<ChatMessage[]>([])
  const loading = ref(false)
  const historyLoading = ref(false)
  const chatLoading = ref(false)

  const sessionFilters = reactive({ first_name: '', last_name: '', dob: '', payer_name: '', from: '', to: '' })

  // The scope is a parameter, not this store's own state: this store outlives Patient History, so
  // keeping the choice here meant a visit to that page silently changed the scope this same request
  // is made with from the workspace sidebar. The sidebar's own default is the value below.
  // The latest request wins: a slower response for an earlier scope must not overwrite history
  // after a newer one has already landed.
  let sessionsGeneration = 0

  async function fetchSessions(scope: 'mine' | 'all' = 'mine') {
    const generation = ++sessionsGeneration

    historyLoading.value = true
    try {
      const params = new URLSearchParams({ scope })
      if (sessionFilters.first_name)
        params.set('first_name', sessionFilters.first_name)
      if (sessionFilters.last_name)
        params.set('last_name', sessionFilters.last_name)
      if (sessionFilters.dob)
        params.set('dob', sessionFilters.dob)
      if (sessionFilters.payer_name)
        params.set('payer', sessionFilters.payer_name)
      if (sessionFilters.from)
        params.set('from', sessionFilters.from)
      if (sessionFilters.to)
        params.set('to', sessionFilters.to)
      const { data } = await api.get(`/insurance/verifications?${params}`)

      if (generation === sessionsGeneration)
        history.value = data.data ?? data
    } finally {
      if (generation === sessionsGeneration)
        historyLoading.value = false
    }
  }

  async function fetchHistory(scope: 'mine' | 'all' = 'mine') {
    return fetchSessions(scope)
  }

  /**
   * Emit the completion of a verification run (VH-2672).
   *
   * The three-way branch is not stylistic. `metadata: { result: status }`
   * would be rejected by the no-non-literal-track-event-metadata rule even
   * though `status` is typed to exactly these three values — the rule has no
   * type information, and "it's typed safely today" is precisely the
   * assumption that stops holding when a type widens later. Branching to
   * literals is the cost of that guarantee, and it keeps every value that can
   * reach the event stream visible in this file.
   */
  function trackVerificationResult(
    screen: BehaviorScreen,
    attemptId: string,
    status: VerificationSession['status'],
    elapsedMs: number,
  ) {
    const durationMs = Math.round(elapsedMs)

    if (status === 'success') {
      trackEvent(BehaviorEventName.ActionCompleted, screen, {
        action: BehaviorAction.VerificationRun,
        metadata: { result: 'success' },
        attemptId,
        durationMs,
      })
    } else if (status === 'failed') {
      trackEvent(BehaviorEventName.ActionCompleted, screen, {
        action: BehaviorAction.VerificationRun,
        metadata: { result: 'failed' },
        attemptId,
        durationMs,
      })
    } else {
      trackEvent(BehaviorEventName.ActionCompleted, screen, {
        action: BehaviorAction.VerificationRun,
        metadata: { result: 'incomplete' },
        attemptId,
        durationMs,
      })
    }
  }

  /**
   * The result the server recorded for a verify that came back as an error, or
   * null when it recorded none (VH-2811).
   *
   * A 422 carries `verification_result` when the payer answered — `failed` for
   * a rejection, `incomplete` for an outage — and that is a run with a result,
   * not an abandoned one. Without it, every rejected member ID counted as a
   * walk-away, and verification read as 6 runs in 7 abandoned when the payer
   * had answered 5 of them. No field means nothing was recorded: a validation
   * error, a connection failure, an unexpected server error.
   */
  function recordedResult(error: unknown): 'failed' | 'incomplete' | null {
    const result = (error as { response?: { data?: { verification_result?: unknown } } })
      ?.response?.data?.verification_result

    return (result === 'failed' || result === 'incomplete') ? result : null
  }

  /**
   * One verification run as Funnel 1 sees it: started, then completed with its
   * result, or abandoned when it produced none.
   *
   * Shared so that verify() and a user-initiated verifyRaw() cannot drift into
   * two different definitions of a run. Before VH-2811 only verify() had this,
   * and WorkspacePage sends most user verifications through verifyRaw() — so
   * re-verifying a plan, verifying a sibling coverage and bulk-verifying
   * unverified plans were all invisible.
   */
  async function trackedVerification<T extends VerificationSession | null | undefined>(
    run: () => Promise<T>,
  ): Promise<T> {
    // Resolved once, before the await: the user can navigate away mid-run and
    // the completion must be attributed to the screen they started on.
    const screen = activeScreen()
    const startedAt = performance.now()

    // One id for the run's start and its outcome, so the server can pair them.
    const attemptId = newAttemptId()

    if (screen)
      trackEvent(BehaviorEventName.ActionStarted, screen, { action: BehaviorAction.VerificationRun, attemptId })

    try {
      const result = await run()

      // Funnel 1 (VH-2672). The session status is our interaction with the
      // payer, not the patient's coverage — see BehaviorMetadataByAction.
      if (screen && result)
        trackVerificationResult(screen, attemptId, result.status, performance.now() - startedAt)

      return result
    } catch (error) {
      const result = recordedResult(error)

      // The payer answered, through an error response: a completed run.
      if (screen && result) {
        trackVerificationResult(screen, attemptId, result, performance.now() - startedAt)
      } else if (screen) {
        // Abandoned: a run the user started and got no answer from, which is
        // exactly the drop-off a funnel that only counted successes would hide.
        trackEvent(BehaviorEventName.ActionAbandoned, screen, {
          action: BehaviorAction.VerificationRun,
          attemptId,
          durationMs: Math.round(performance.now() - startedAt),
        })
      }

      throw error
    }
  }

  async function verify(payload: {
    first_name: string
    last_name: string
    dob: string
    member_id: string | null
    payer_id: string | null
    insurance_company: string | null
    service_types: ServiceType[]
    ssn?: string
    state?: string
    location_npi?: string | null

    // VH-3175: Y-m-d. The payer answers for today when absent.
    date_of_service?: string | null

    // VH-2241 round 13 bug fix: optional so existing verify() callers (submitVerification,
    // selectCoverage) are unaffected, but WorkspacePage.vue's verifyActiveCoverage now passes
    // this through for its "brand-new session tree" branch — without it, the resulting
    // VerificationSession was never linked back to the DiscoverySession it came from
    // (discovery_session_uuid stayed null server-side), which broke restoring an unverified
    // sibling's active_payer_id query param after a page refresh: there was no
    // discovery_session_uuid on the loaded session to re-fetch the sibling coverage list from.
    discovery_session_uuid?: string | null

    // VH-2278: the patient already identified (found on file or picked on the lookup's "did
    // you mean" screen), so the server links the session to them instead of re-matching.
    patient_uuid?: string | null
  }) {
    loading.value = true

    try {
      // Every verify() is user-initiated: its callers are submitVerification,
      // selectCoverage and verifyActiveCoverage, all behind a click.
      return await trackedVerification(async () => {
        // Real eligibility round-trips can run well past the default 30s client timeout (measured
        // this session: p95 ~6.6s but max ~110s for a single payer round-trip). 195s stays above
        // the server's own ceiling (VH-2491: ALB idle_timeout 190s, itself sized for
        // StediService's 130s Guzzle timeout plus its own up-to-30s concurrency-slot wait), so
        // the FE never gives up before the backend possibly would have responded.
        const { data } = await api.post('/insurance/verify', payload, { timeout: 195000 })

        session.value = data.data ?? data
        chatMessages.value = []
        if (session.value)
          history.value.unshift(session.value)

        return session.value
      })
    } finally {
      loading.value = false
    }
  }

  // Bug fix: chat history is scoped more restrictively than the plan/session data around it --
  // ChatController::resolveSession() 404s when the provider has session-sharing disabled and the
  // requested session's BedrockSession belongs to a verification run by a different user (see
  // server app/Http/Controllers/Insurance/ChatController.php). That's an expected, non-fatal
  // "this session's chat isn't visible to you" outcome, not a reason to abort rendering the plan
  // itself -- this used to throw uncaught, which aborted selectSession() before it could flip
  // WorkspacePage's `view` to 'result', leaving the bare verification form on screen for any
  // plan the current user didn't personally verify.
  async function loadHistory(bedrockSessionUuid: string) {
    try {
      const { data } = await api.get(`/insurance/chat/${bedrockSessionUuid}/history`)

      chatMessages.value = data.messages
    } catch {
      chatMessages.value = []
    }
  }

  async function ask(bedrockSessionUuid: string, question: string) {
    chatLoading.value = true

    chatMessages.value.push({
      uuid: crypto.randomUUID(),
      role: 'user',
      content: question,
      created_at: new Date().toISOString(),
    })

    try {
      const { data } = await api.post(`/insurance/chat/${bedrockSessionUuid}`, { question })

      chatMessages.value.push(data.message)
    } catch (err) {
      chatMessages.value.pop()
      throw err
    } finally {
      chatLoading.value = false
    }
  }

  async function verifyRaw(payload: {
    first_name: string
    last_name: string
    dob: string
    member_id: string | null
    payer_id: string | null
    insurance_company: string | null
    service_types: ServiceType[]
    discovery_session_uuid?: string | null
    location_npi?: string | null
    date_of_service?: string | null
    source_verification_session_uuid?: string

    // VH-2495: when set (and payer_id isn't also supplied), the server uses this carrier's
    // cached Stedi payer_id directly instead of falling back to expensive payer-name matching.
    insurance_carrier_uuid?: string | null

    // VH-2278: see verify().
    patient_uuid?: string | null

    // A plan on file. With no payer_id, the server verifies it against the payer it resolves
    // for that plan instead of the plan's raw carrier label.
    insurance_uuid?: string | null
  }, options: { trigger: VerificationTrigger; skipRouteChangeCancel?: boolean }): Promise<VerificationSession> {
    const run = async (): Promise<VerificationSession> => {
      const { data } = await api.post('/insurance/verify', payload, {
        skipRouteChangeCancel: options?.skipRouteChangeCancel,

        // See verify()'s identical comment above -- same slow-backend ceiling.
        timeout: 195000,
      })

      const s: VerificationSession = data.data ?? data

      history.value.unshift(s)

      return s
    }

    // Only a run a person asked for enters the funnel. The automatic callers —
    // triggerMaAutoVerify and the autoVerify* watchers — are the product doing
    // work on the user's behalf; counting them would inflate starts with system
    // activity, the same line the verification-activity tool draws between
    // `native` and `auto_batch` sessions.
    //
    // `?.` on purpose, even though the type says `options` is required. CI does
    // not type-check this repo, so a caller that omits it would compile — and
    // without the `?.` an instrumentation argument could crash a real payer
    // verification, which is the one thing telemetry must never do. An omitted
    // trigger is therefore treated as untracked, and the call-site test below
    // is what actually stops one being merged.
    return options?.trigger === 'user' ? trackedVerification(run) : run()
  }

  async function submitFeedback(bedrockSessionUuid: string, messageUuid: string, rating: 1 | -1) {
    await api.post(`/insurance/chat/${bedrockSessionUuid}/feedback`, {
      message_uuid: messageUuid,
      rating,
    })

    const msg = chatMessages.value.find(m => m.uuid === messageUuid)
    if (msg)
      msg.feedback = rating
  }

  async function restartChat(bedrockSessionUuid: string) {
    await api.post(`/insurance/chat/${bedrockSessionUuid}/restart`)
    chatMessages.value = []
  }

  async function regenerateSummary(uuid: string): Promise<void> {
    const { data } = await api.post(`/insurance/verify/${uuid}/regenerate-summary`)

    session.value = data.data ?? data
  }

  async function fetchSession(uuid: string): Promise<VerificationSession> {
    const { data } = await api.get(`/insurance/verify/${uuid}`)

    return data.data ?? data
  }

  function clearSession() {
    session.value = null
    chatMessages.value = []
  }

  // Logout / account switch: drop every PHI-bearing ref (clearSession only clears the selected session).
  function resetForLogout() {
    clearSession()
    history.value = []
    loading.value = false
    historyLoading.value = false
    chatLoading.value = false
    Object.assign(sessionFilters, { first_name: '', last_name: '', dob: '', payer_name: '', from: '', to: '' })
  }

  async function selectSession(s: VerificationSession) {
    session.value = s
    chatMessages.value = []
    if (s.bedrock_session_uuid)
      await loadHistory(s.bedrock_session_uuid)
  }

  return {
    session,
    history,
    chatMessages,
    loading,
    historyLoading,
    chatLoading,
    sessionFilters,
    fetchSessions,
    fetchHistory,
    verify,
    verifyRaw,
    selectSession,
    loadHistory,
    ask,
    restartChat,
    clearSession,
    resetForLogout,
    submitFeedback,
    regenerateSummary,
    fetchSession,
  }
})
